/**
 * A tela final — a razão de o produto existir.
 *
 * **Cada `fim` tem a sua mensagem e as suas instruções.** Quem confirmou lê
 * como chegar; quem recusou lê um agradecimento; quem ficou em dúvida lê que
 * será procurado. É isso que "instruções diferentes por cenário" quer dizer, e
 * é por isso que nada aqui é texto fixo nosso além dos rótulos de estrutura.
 *
 * O título é `no.rotulo` — o do NÓ, não o do formulário. Trocar os dois faria
 * toda despedida repetir o nome do evento no lugar da despedida.
 *
 * **O botão é a terceira peça, e é a única que age.** "Não recebeu o convite?
 * Entre em contato comigo pelo WhatsApp" era uma instrução sem com o quê: o
 * convidado lia, e teria de sair procurando o telefone de alguém. O endereço
 * dele vem de `config.acao`, e passa por `lerAcaoDoFim` antes de virar `href` —
 * o React escapa conteúdo, não esquema, e um `javascript:` gravado ali rodaria
 * com o clique de quem só ia confirmar presença.
 */

import type { NoDoConvite } from '../../tipos';
import type { Trilho } from '../../ganchos/useConvite';
import { abreEmAbaNova, lerAcaoDoFim } from '../../util/acao';
import { Trilha } from './Trilha';

export type PropsDoFim = {
  readonly no: NoDoConvite | null;
  readonly mensagem: string | null;
  readonly instrucoes: string | null;
  readonly trilha: readonly Trilho[];
  /** `true` quando o navegador lembrou de uma resposta já concluída. */
  readonly retomada?: boolean;
  /** O e-mail desta resposta, já mascarado, ou `null` se não houver. */
  readonly emailMascarado?: string | null;
  /** Descarta a resposta lembrada e abre um formulário limpo. */
  readonly aoNaoSerVoce?: () => void;
};

export function Fim({
  no,
  mensagem,
  instrucoes,
  trilha,
  retomada = false,
  emailMascarado = null,
  aoNaoSerVoce,
}: PropsDoFim) {
  const titulo = no?.rotulo.trim() ?? '';
  const acao = lerAcaoDoFim(no?.config);

  return (
    <section className="fim">
      {/* Quem ACABOU de enviar lê "Resposta enviada". Quem VOLTOU precisa de
          outra frase: a dele já está registrada, e ele não deve responder de
          novo. É a distinção que este recurso inteiro existe para fazer —
          `retomada` vem do gancho, que sabe se a resposta foi lembrada pelo
          navegador ou acabou de ser dada. */}
      <p className="fim__selo">{retomada ? 'Resposta já registrada' : 'Resposta enviada'}</p>

      {retomada ? <JaRespondeu email={emailMascarado} aoNaoSerVoce={aoNaoSerVoce} /> : null}

      {titulo !== '' ? <h2 className="fim__titulo">{titulo}</h2> : null}

      {mensagem ? <p className="fim__mensagem texto-do-organizador">{mensagem}</p> : null}

      {instrucoes ? (
        <div className="fim__instrucoes">
          <h3 className="fim__instrucoes-rotulo">Instruções</h3>
          <p className="texto-do-organizador">{instrucoes}</p>
        </div>
      ) : null}

      {acao ? (
        <p className="fim__acao">
          <a
            className="botao botao--primario botao--link"
            href={acao.url}
            // `rel` vai junto do `target` e não sozinho: sem `noopener`, a
            // página aberta ganha `window.opener` e pode reescrever esta aba —
            // que é justamente a que tem as instruções de chegada.
            {...(abreEmAbaNova(acao.url) ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
          >
            {acao.rotulo}
          </a>
        </p>
      ) : null}

      {/* A trilha some numa RETOMADA, e essa foi a correção mais importante
          deste recurso.

          Ela lista o que a pessoa respondeu — nome, e-mail, o que ela escreveu
          no campo aberto. Mascarar o e-mail no aviso acima e exibir o endereço
          inteiro dois blocos abaixo seria teatro: quem pegou o celular
          emprestado leria tudo. Um teste que afirmava "o e-mail não aparece
          inteiro" foi quem pegou isso, reprovando na trilha.

          Para quem ACABOU de responder ela continua — é o resumo do que ele
          acabou de enviar, e ele é o dono daquilo. */}
      {retomada ? null : <Trilha itens={trilha} />}
    </section>
  );
}

/**
 * O aviso de quem voltou, e a saída para quem não é essa pessoa.
 *
 * ── Por que mostrar o e-mail, e por que mascarado ───────────────────────────
 *
 * Sem nenhuma pista de QUEM respondeu, "já registrada" é inútil no caso que
 * importa: o celular emprestado, onde a resposta é de outra pessoa e quem está
 * olhando precisa reconhecer isso para clicar em "não sou eu". Inteiro,
 * entregaria o endereço de um terceiro a quem pegou o aparelho. Duas letras e o
 * domínio resolvem os dois lados.
 *
 * Quando o formulário não pergunta e-mail, não há o que mostrar — e a tela diz
 * só que a resposta está registrada, que é tudo o que ela pode afirmar
 * honestamente.
 *
 * ── Por que o botão não é um "apagar" ───────────────────────────────────────
 *
 * Ele não toca no banco. A resposta de quem respondeu antes continua contada e
 * exportável — deixar um clique de estranho destruí-la seria pior que o problema
 * que estamos resolvendo. O que ele desfaz é o vínculo entre ESTE navegador e
 * aquela resposta.
 */
function JaRespondeu({
  email,
  aoNaoSerVoce,
}: {
  readonly email: string | null;
  readonly aoNaoSerVoce?: () => void;
}) {
  return (
    <div className="ja-respondeu" role="status">
      <p className="ja-respondeu__texto">
        {email === null ? (
          'Este formulário já foi respondido neste navegador.'
        ) : (
          <>
            Já respondemos por <strong className="ja-respondeu__email">{email}</strong> neste
            navegador.
          </>
        )}
      </p>

      {aoNaoSerVoce ? (
        <button type="button" className="botao botao--discreto" onClick={aoNaoSerVoce}>
          Não sou eu — responder de novo
        </button>
      ) : null}
    </div>
  );
}
